import { IS_ARTIST } from '@disco/constants/FeatureTypes'
import { ONBOARDING_SPLASH_ENABLED } from '@disco/constants/onboarding'
import type { Group } from 'types/group'
import type { State } from 'types/redux'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'

// How many requests are assumed to be made before the total is known
const MIN_ASSUMED_REQUESTS = 15

/**
 * Gets the currently selected library group from the app state.
 * @param state - The Redux state
 * @returns The selected library group, or null/undefined if none is selected
 */
export const getSelectedLibrary = (state: State): Group | null | undefined =>
  state.app.selectedLibrary
/**
 * Gets whether the app has been initialized.
 * @param state - The Redux state
 * @returns True if the app is initialized, false otherwise
 */
export const getAppInitialized = (state: State): boolean =>
  state.app.initialized
/**
 * Checks if the current device type is a phone (small screen).
 * @param state - The Redux state
 * @returns True if the device is a phone, false otherwise
 * @todo Rename isDeviceTypePhone to something more meaningful, such as isDevicePhone
 */
export const isDeviceTypePhone = (state: State): boolean => state.app.screen.sm
/**
 * Checks if onboarding is enabled for the current user.
 * Onboarding is only enabled for artists when the feature flag is enabled.
 * @param state - The Redux state
 * @returns True if onboarding is enabled, false otherwise
 */
export const getIsOnboardingEnabled = (state: State): boolean => {
  // Onboarding is only enabled for artists
  return (
    ONBOARDING_SPLASH_ENABLED &&
    isFeatureEnabled(state, {
      featureName: IS_ARTIST,
    })
  )
}
/**
 * Checks if the user is currently in the onboarding flow.
 * @param state - The Redux state
 * @returns True if the user is actively onboarding, false otherwise
 */
export const getIsOnboarding = (state: State): boolean =>
  getIsOnboardingEnabled(state) &&
  state.app.isOnboarding &&
  !state.app.isOnboardingSkipped

export const getIsOnboardingSkipped = (state: State): boolean =>
  state.app.isOnboardingSkipped

/**
 * Gets the number of completed requests from the app state.
 * @param state - The Redux state
 * @returns The number of completed requests
 */
export const getRequestsCompleted = (state: State): number =>
  state.app.requestsCompleted
/**
 * Gets the total number of requests, ensuring it's at least the minimum assumed requests.
 * @param state - The Redux state
 * @returns The total number of requests, at least MIN_ASSUMED_REQUESTS
 */
export const getRequestsTotal = (state: State): number =>
  Math.max(state.app.requestsTotal, MIN_ASSUMED_REQUESTS)

export const getPlaylistCreatorMaxWidth = (state: State): number =>
  state.app.playlistCreatorWidth

export const getShortlistsOpen = (state: State): boolean =>
  state.app.shortlistsOpen
